import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';

const LIGAS_DO_LOCAL = gql`
  query LigasDoLocal($localId: ID!) {
    ligasDoLocal(localId: $localId) {
      id
      liga { id nome imagemUrl }
      criadoEm
    }
  }
`;

const HISTORICO_DESCONTO = gql`
  query HistoricoDescontoAluno($localId: ID!) {
    historicoDescontoAluno(localId: $localId) {
      id
      percentualAnterior
      percentualNovo
      alteradoEm
      usuario { id nome }
    }
  }
`;

const VINCULAR_LIGA = gql`
  mutation VincularLigaAoLocal($localId: ID!, $ligaId: ID!) {
    vincularLigaAoLocal(localId: $localId, ligaId: $ligaId) {
      id
      liga { id nome imagemUrl }
    }
  }
`;

const DESVINCULAR_LIGA = gql`
  mutation DesvincularLigaDoLocal($localId: ID!, $ligaId: ID!) {
    desvincularLigaDoLocal(localId: $localId, ligaId: $ligaId)
  }
`;

const SALVAR_DESCONTO = gql`
  mutation SalvarDescontoAluno($localId: ID!, $percentual: Float) {
    salvarDescontoAluno(localId: $localId, percentual: $percentual) {
      id
      percentualDescontoAluno
    }
  }
`;

export interface LigaLocalItem {
  id: string;
  liga: { id: string; nome: string; imagemUrl?: string | null };
  criadoEm?: string;
}

export interface DescontoLog {
  id: string;
  percentualAnterior?: number | null;
  percentualNovo?: number | null;
  alteradoEm: string;
  usuario: { id: string; nome: string };
}

function extractMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Erro inesperado';
}

const DESCONTO_ALUNO_PARA_USUARIO = gql`
  query DescontoAlunoParaUsuario($localId: ID!) {
    descontoAlunoParaUsuario(localId: $localId)
  }
`;

export const buscarDescontoAlunoParaUsuario = async (localId: string): Promise<number | null> => {
  try {
    const { data } = await apolloClient.query({
      query: DESCONTO_ALUNO_PARA_USUARIO,
      variables: { localId },
      fetchPolicy: 'network-only',
    });
    return data?.descontoAlunoParaUsuario ?? null;
  } catch (error) {
    return null;
  }
};

export const buscarLigasDoLocal = async (localId: string): Promise<LigaLocalItem[]> => {
  try {
    const { data } = await apolloClient.query({
      query: LIGAS_DO_LOCAL,
      variables: { localId },
      fetchPolicy: 'network-only',
    });
    return data?.ligasDoLocal ?? [];
  } catch (error) {
    dispatchGlobalError(extractMessage(error));
    return [];
  }
};

export const buscarHistoricoDesconto = async (localId: string): Promise<DescontoLog[]> => {
  try {
    const { data } = await apolloClient.query({
      query: HISTORICO_DESCONTO,
      variables: { localId },
      fetchPolicy: 'network-only',
    });
    return data?.historicoDescontoAluno ?? [];
  } catch (error) {
    dispatchGlobalError(extractMessage(error));
    return [];
  }
};

export const vincularLigaAoLocal = async (localId: string, ligaId: string): Promise<LigaLocalItem | null> => {
  try {
    const { data } = await apolloClient.mutate({ mutation: VINCULAR_LIGA, variables: { localId, ligaId } });
    return data?.vincularLigaAoLocal ?? null;
  } catch (error) {
    dispatchGlobalError(extractMessage(error));
    return null;
  }
};

export const desvincularLigaDoLocal = async (localId: string, ligaId: string): Promise<boolean> => {
  try {
    await apolloClient.mutate({ mutation: DESVINCULAR_LIGA, variables: { localId, ligaId } });
    return true;
  } catch (error) {
    dispatchGlobalError(extractMessage(error));
    return false;
  }
};

export const salvarDescontoAluno = async (localId: string, percentual: number | null): Promise<boolean> => {
  try {
    await apolloClient.mutate({ mutation: SALVAR_DESCONTO, variables: { localId, percentual } });
    return true;
  } catch (error) {
    dispatchGlobalError(extractMessage(error));
    return false;
  }
};
